/* ==========================================================================
   Lo La cart page — behaviour for sections/main-cart-items.liquid.

   assets/cart.js still owns quantity changes, removals and gift wrap: it
   swaps the innerHTML of <cart-items class="js-contents"> after each change.
   This file adds everything else and survives those swaps by delegating
   events from <lola-cart> (which is never replaced) and re-applying UI state
   after every swap through a MutationObserver.
   ========================================================================== */
(() => {
  if (customElements.get("lola-cart")) return;

  const WISHLIST_KEY = "glozin__wishlist-items";
  const SAVED_VARIANTS_KEY = "lola__saved-variants";
  const REWARDS_KEY = "lola__cart-rewards-collapsed";

  const readJSON = (key, fallback) => {
    try {
      const value = JSON.parse(localStorage.getItem(key));
      return value ?? fallback;
    } catch (e) {
      return fallback;
    }
  };
  const writeJSON = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  };

  const postJSON = (url, body) =>
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
      lolaCart: true,
    }).then((response) => response.json());

  const refreshLazyImages = () => {
    if (window.BlsLazyloadImg?.init) window.BlsLazyloadImg.init();
    if (typeof window.initLazyloadItem === "function") window.initLazyloadItem();
  };

  const updateCartCount = (count) => {
    document.querySelectorAll(".cart-count").forEach((el) => {
      el.innerHTML = el.classList.contains("cart-count-drawer")
        ? `(${count})`
        : count > 100
          ? "~"
          : count;
    });
  };

  // cart.js treats every `change` inside <cart-items> as a quantity update.
  // The coupon, gift and note fields now live in there too, so only let
  // quantity inputs through.
  customElements.whenDefined("cart-items").then(() => {
    const CartItems = customElements.get("cart-items");
    const onChange = CartItems.prototype.onChange;
    if (onChange.__lolaPatched) return;
    CartItems.prototype.onChange = function (event) {
      if (event.target?.matches?.('[name="updates[]"]')) onChange.call(this, event);
    };
    CartItems.prototype.onChange.__lolaPatched = true;
  });

  class LolaCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.id;
      this.openRows = new Set(
        Array.from(this.querySelectorAll("details[data-row][open]"), (d) => d.dataset.row)
      );
      this.savedHTML = null;

      this.addEventListener("click", this.onClick.bind(this));
      this.addEventListener("submit", this.onSubmit.bind(this));
      this.addEventListener("change", this.onChange.bind(this));
      this.addEventListener("input", this.onInput.bind(this));
      this.addEventListener("toggle", this.onToggle.bind(this), true);

      this.observer = new MutationObserver(() => this.afterRender());
      this.observer.observe(this, { childList: true, subtree: true });

      this.patchFetch();
      this.afterRender();
      this.loadSaved();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    get cartItems() {
      return this.querySelector("cart-items");
    }

    /* ---------- State that must survive cart.js re-renders ---------- */

    afterRender() {
      if (this.rendering) return;
      this.rendering = true;

      // Keep coupon / note rows open across re-renders.
      this.querySelectorAll("details[data-row]").forEach((details) => {
        if (this.openRows.has(details.dataset.row)) details.open = true;
      });

      // Rewards banner collapse is remembered per browser.
      const collapsed = readJSON(REWARDS_KEY, false);
      this.toggleAttribute("data-rewards-collapsed", collapsed);
      this.querySelectorAll("[data-rewards-toggle]").forEach((btn) =>
        btn.setAttribute("aria-expanded", String(!collapsed))
      );

      // Re-fill the saved list; its rows are fetched client-side.
      const list = this.querySelector("[data-saved-list]");
      if (list && !list.childElementCount && this.savedHTML) {
        list.innerHTML = this.savedHTML;
        list.closest("[data-saved]").hidden = false;
      }

      this.querySelectorAll(".lola-carousel[data-recs-url]:not([data-loaded])").forEach(
        (carousel) => this.loadRecommendations(carousel)
      );
      this.querySelectorAll(".lola-carousel").forEach((carousel) =>
        this.updateArrows(carousel)
      );

      this.rendering = false;
    }

    /* ---------- Section rendering ---------- */

    // Re-render the cart from a Section Rendering API payload.
    render(sectionHTML) {
      if (!sectionHTML) return;
      const doc = new DOMParser().parseFromString(sectionHTML, "text/html");
      const next = doc.querySelector("cart-items");
      const current = this.cartItems;
      const wasEmpty = current?.classList.contains("is-empty");
      const isEmpty = next?.classList.contains("is-empty");

      if (!next || !current || wasEmpty !== isEmpty) {
        // Layout changes between empty / filled: swap the whole section.
        const root = doc.getElementById("main-cart-items");
        if (root) {
          this.innerHTML = root.innerHTML;
          this.classList.toggle("is-empty", !!isEmpty);
          refreshLazyImages();
        }
        return;
      }
      current.innerHTML = next.innerHTML;
      // cart.js binds the gift-wrap checkbox directly and only re-binds after
      // its own updates; flag this one so onChange handles it instead.
      const giftWrap = this.querySelector("#gift_form_minicart");
      if (giftWrap) giftWrap.dataset.lolaBound = "true";
    }

    async refresh() {
      const url = `${window.routes?.cart_url || "/cart"}?section_id=${this.sectionId}`;
      const html = await fetch(url, { lolaCart: true }).then((r) => r.text());
      this.render(html);
      const cart = await fetch(`${window.routes?.cart_url || "/cart"}.js`, { lolaCart: true }).then(
        (r) => r.json()
      );
      updateCartCount(cart.item_count);
    }

    // Adds made elsewhere on the page (carousel cards, the ATC sheet) should
    // update this cart without a reload.
    patchFetch() {
      if (window.__lolaCartFetchPatched) return;
      window.__lolaCartFetchPatched = true;
      const originalFetch = window.fetch;
      const self = this;
      window.fetch = function (resource, options) {
        const promise = originalFetch.apply(this, arguments);
        const url = typeof resource === "string" ? resource : resource?.url || "";
        if (!options?.lolaCart && url.includes("/cart/add")) {
          promise
            .then((response) => {
              if (!response.ok) return;
              clearTimeout(self.refreshTimer);
              self.refreshTimer = setTimeout(() => self.refresh().catch(() => {}), 350);
            })
            .catch(() => {});
        }
        return promise;
      };
    }

    setLoading(on) {
      this.classList.toggle("is-busy", on);
    }

    /* ---------- Events ---------- */

    onClick(event) {
      const target = event.target;

      const rewardsToggle = target.closest("[data-rewards-toggle]");
      if (rewardsToggle) {
        writeJSON(REWARDS_KEY, !this.hasAttribute("data-rewards-collapsed"));
        this.afterRender();
        return;
      }

      const saveLater = target.closest("[data-save-later]");
      if (saveLater) {
        event.preventDefault();
        this.saveForLater(saveLater);
        return;
      }

      const couponRemove = target.closest("[data-coupon-remove]");
      if (couponRemove) {
        event.preventDefault();
        const code = couponRemove.dataset.couponRemove;
        this.applyCodes(this.currentCodes().filter((c) => c !== code));
        return;
      }

      const savedMove = target.closest("[data-saved-move]");
      if (savedMove) {
        event.preventDefault();
        this.moveToBag(savedMove.closest(".lola-saved__item"), savedMove);
        return;
      }

      const savedRemove = target.closest("[data-saved-remove]");
      if (savedRemove) {
        event.preventDefault();
        const row = savedRemove.closest(".lola-saved__item");
        this.removeFromWishlist(row.dataset.productId);
        this.removeSavedRow(row);
        return;
      }

      const arrow = target.closest(".lola-carousel__arrow");
      if (arrow) {
        const track = arrow.closest(".lola-carousel").querySelector("[data-track]");
        const rtl = getComputedStyle(track).direction === "rtl";
        const step = track.clientWidth * 0.8 * Number(arrow.dataset.dir) * (rtl ? -1 : 1);
        track.scrollBy({ left: step, behavior: "smooth" });
        return;
      }

      // Heart on a cart item toggles the wishlist; refresh the saved list.
      if (target.closest(".lola-item__wish")) {
        setTimeout(() => this.loadSaved(), 50);
      }
    }

    onSubmit(event) {
      const form = event.target.closest("[data-coupon-form]");
      if (!form) return;
      event.preventDefault();
      const input = form.querySelector("[data-coupon-input]");
      const code = input.value.trim();
      if (!code) {
        this.couponMessage(this.dataset.couponEmpty);
        input.focus();
        return;
      }
      const codes = this.currentCodes();
      if (!codes.some((c) => c.toLowerCase() === code.toLowerCase())) codes.push(code);
      this.applyCodes(codes, code);
    }

    onChange(event) {
      const giftWrap = event.target.closest("#gift_form_minicart[data-lola-bound]");
      if (giftWrap) {
        if (giftWrap.checked) this.cartItems?.addGiftwrapCartClick(giftWrap);
        else this.cartItems?.updateQuantity(giftWrap.dataset.index, 0);
        return;
      }

      const toggle = event.target.closest("[data-gift-toggle]");
      if (toggle) {
        const row = toggle.closest(".lola-gift");
        const message = row.querySelector("[data-gift-message]");
        row.classList.toggle("is-on", toggle.checked);
        if (!toggle.checked && message) message.value = "";
        this.updateAttributes({
          "Gift order": toggle.checked ? "Yes" : "",
          "Gift message": toggle.checked && message ? message.value : "",
        });
        if (toggle.checked && message) setTimeout(() => message.focus(), 150);
      }
    }

    onInput(event) {
      const message = event.target.closest("[data-gift-message]");
      if (!message) return;
      clearTimeout(this.giftTimer);
      this.giftTimer = setTimeout(
        () => this.updateAttributes({ "Gift message": message.value }),
        400
      );
    }

    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.dataset.row) return;
      if (this.rendering) return;
      if (details.open) this.openRows.add(details.dataset.row);
      else this.openRows.delete(details.dataset.row);
    }

    /* ---------- Coupons ---------- */

    currentCodes() {
      return Array.from(this.querySelectorAll(".lola-coupon__code[data-code]"), (el) => el.dataset.code);
    }

    couponMessage(text) {
      const el = this.querySelector("[data-coupon-message]");
      if (!el) return;
      el.textContent = text || "";
      el.hidden = !text;
    }

    async applyCodes(codes, newCode) {
      this.openRows.add("coupon");
      this.setLoading(true);
      try {
        const cart = await postJSON(`${window.routes?.cart_update_url || "/cart/update"}.js`, {
          discount: codes.join(","),
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        });
        if (cart.status && cart.status >= 400) throw new Error(cart.description);
        this.render(cart.sections?.[this.sectionId]);
        if (newCode) {
          const applied = (cart.discount_codes || []).find(
            (d) => d.code.toLowerCase() === newCode.toLowerCase()
          );
          if (!applied || !applied.applicable) {
            // Shopify keeps a rejected code on the cart but leaves it out of
            // Liquid's cart.discount_codes, so there'd be no chip to remove it.
            // Drop it right away and keep only the codes that were already on.
            const kept = codes.filter((c) => c.toLowerCase() !== newCode.toLowerCase());
            const retry = await postJSON(`${window.routes?.cart_update_url || "/cart/update"}.js`, {
              discount: kept.join(","),
              sections: [this.sectionId],
              sections_url: window.location.pathname,
            });
            this.render(retry.sections?.[this.sectionId]);
            this.couponMessage(this.dataset.couponInvalid);
          }
        }
      } catch (e) {
        this.couponMessage(this.dataset.couponError);
      } finally {
        this.setLoading(false);
      }
    }

    /* ---------- Gift ---------- */

    updateAttributes(attributes) {
      return postJSON(`${window.routes?.cart_update_url || "/cart/update"}.js`, { attributes }).catch(
        () => {}
      );
    }

    /* ---------- Save for later / saved list ---------- */

    saveForLater(button) {
      const { productId, variantId, index } = button.dataset;
      const wishlist = readJSON(WISHLIST_KEY, []);
      if (!wishlist.includes(productId)) wishlist.unshift(productId);
      writeJSON(WISHLIST_KEY, wishlist);

      const variants = readJSON(SAVED_VARIANTS_KEY, {});
      variants[productId] = variantId;
      writeJSON(SAVED_VARIANTS_KEY, variants);

      window.wishlistHeader?.init?.();
      this.cartItems?.updateQuantity(index, 0);
      this.loadSaved();
    }

    removeFromWishlist(productId) {
      writeJSON(
        WISHLIST_KEY,
        readJSON(WISHLIST_KEY, []).filter((id) => id !== productId)
      );
      const variants = readJSON(SAVED_VARIANTS_KEY, {});
      delete variants[productId];
      writeJSON(SAVED_VARIANTS_KEY, variants);
      window.wishlistHeader?.init?.();
    }

    removeSavedRow(row) {
      const list = row.parentElement;
      row.remove();
      this.savedHTML = list.innerHTML;
      if (!list.childElementCount) {
        this.savedHTML = null;
        list.closest("[data-saved]").hidden = true;
      }
    }

    async loadSaved() {
      const wrap = this.querySelector("[data-saved]");
      if (!wrap) return;
      const ids = readJSON(WISHLIST_KEY, []).filter(Boolean).slice(0, 12);
      if (!ids.length) {
        this.savedHTML = null;
        wrap.hidden = true;
        wrap.querySelector("[data-saved-list]").innerHTML = "";
        return;
      }
      const query = ids.map((id) => `id:${id}`).join(" OR ");
      const url =
        `${this.dataset.searchUrl}?section_id=${this.dataset.savedSection}` +
        `&type=product&options%5Bunavailable_products%5D=last&q=${encodeURIComponent(query)}`;
      try {
        const html = await fetch(url).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, "text/html");
        const rows = Array.from(doc.querySelectorAll(".lola-saved__item"));
        // Keep the order the shopper saved them in (newest first).
        rows.sort((a, b) => ids.indexOf(a.dataset.productId) - ids.indexOf(b.dataset.productId));
        const list = this.querySelector("[data-saved-list]");
        if (!list) return;
        list.replaceChildren(...rows);
        this.savedHTML = rows.length ? list.innerHTML : null;
        list.closest("[data-saved]").hidden = !rows.length;
      } catch (e) {
        wrap.hidden = true;
      }
    }

    async moveToBag(row, button) {
      const saved = readJSON(SAVED_VARIANTS_KEY, {})[row.dataset.productId];
      const variantIds = (row.dataset.variantIds || "").split(",");
      const variantId = saved && variantIds.includes(String(saved)) ? saved : row.dataset.variantId;
      button.disabled = true;
      this.setLoading(true);
      try {
        const response = await fetch(`${window.routes?.cart_add_url || "/cart/add"}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          lolaCart: true,
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message);
        this.removeFromWishlist(row.dataset.productId);
        this.removeSavedRow(row);
        await this.refresh();
      } catch (e) {
        button.disabled = false;
        if (typeof window.showToast === "function" && e.message) {
          window.showToast(e.message, 4000, "modal-error");
        }
      } finally {
        this.setLoading(false);
      }
    }

    /* ---------- Carousels ---------- */

    async loadRecommendations(carousel) {
      carousel.dataset.loaded = "true";
      const exclude = (carousel.dataset.exclude || "").split(",");
      try {
        const html = await fetch(carousel.dataset.recsUrl).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, "text/html");
        const slides = Array.from(doc.querySelectorAll(".lola-carousel__slide")).filter(
          (slide) => !exclude.includes(slide.dataset.productId)
        );
        if (!slides.length) return;
        carousel.querySelector("[data-track]").replaceChildren(...slides);
        carousel.hidden = false;
        refreshLazyImages();
        this.updateArrows(carousel);
      } catch (e) {}
    }

    updateArrows(carousel) {
      const track = carousel.querySelector("[data-track]");
      if (!track) return;
      if (!track.dataset.bound) {
        track.dataset.bound = "true";
        track.addEventListener("scroll", () => this.updateArrows(carousel), { passive: true });
      }
      const max = track.scrollWidth - track.clientWidth;
      const pos = Math.abs(track.scrollLeft);
      const [prev, next] = carousel.querySelectorAll(".lola-carousel__arrow");
      if (!prev || !next) return;
      prev.disabled = pos <= 2;
      next.disabled = pos >= max - 2;
      carousel.classList.toggle("has-overflow", max > 2);
    }
  }

  customElements.define("lola-cart", LolaCart);
})();
